import type { UseMutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { v4 as uuid } from "uuid";

import { EntityTypes } from "../../EntityTypes";
import { IMediaResource } from "../../media";
import { useApi } from "../../SDKProvider";
import { ITagResource } from "../../tags";
import { unwrapAPIResult } from "../../utils/api";
import { mapRelationalValue, mapRelationalValueAsIRI, RelationalValue } from "../../utils/filters";
import { IResourceResource } from "../types";

export interface IResourceUpdateMutationParameters {
    resource: RelationalValue<IResourceResource>;
    title?: string;
    description?: string;
    url?: string;
    image?: RelationalValue<IMediaResource>;
    tags?: RelationalValue<ITagResource>[];
}

export type IResourceUpdateMutationResult = IResourceResource;

/**
 * Specialized hook for updating a resource with automatic image upload.
 */
export const useResourceUpdateMutation = (
    options: UseMutationOptions<IResourceUpdateMutationResult, unknown, IResourceUpdateMutationParameters> = {},
) => {
    const api = useApi();

    const mutationFn = async (params: IResourceUpdateMutationParameters) => {
        const { resource, title, description, url, image, tags } = params;

        // Automatically upload image if URL has changed
        let mediaIRI = image ? mapRelationalValueAsIRI(EntityTypes.MEDIA, image) : undefined;

        if (url && !image) {
            try {
                // Retrieve preview image from URL
                const { data: imageBlob } = await axios.get("/api/website-image", {
                    params: { url },
                    responseType: "blob",
                });

                // Upload image as media
                const formData = new FormData();
                formData.append("file", imageBlob);
                formData.append("uuid", uuid());

                const mediaResponse = await api.request<IMediaResource>({
                    url: "/media",
                    method: "POST",
                    headers: { "Content-Type": "multipart/form-data" },
                    data: formData,
                });

                mediaIRI = mediaResponse.data["@id"];
            } catch {
                mediaIRI = undefined;
            }
        }

        // Update resource
        return unwrapAPIResult(
            api.request<IResourceUpdateMutationResult>({
                method: "PUT",
                url: `/resources/${mapRelationalValue(resource)}`,
                data: {
                    ...(title !== undefined && { title }),
                    ...(description !== undefined && { description }),
                    ...(url !== undefined && { url }),
                    ...(mediaIRI !== undefined && { image: mediaIRI }),
                    ...(tags !== undefined && { tags: tags.map(tag => mapRelationalValueAsIRI(EntityTypes.TAG, tag)) }),
                },
            }),
        );
    };

    return useMutation(mutationFn, options);
};
